import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SaveIcon, XIcon, PlusIcon, UploadIcon, MoreVerticalIcon, ChevronLeftIcon, ImportIcon } from 'lucide-react';
import PageHeader from '../components/PageHeader';
interface ScriptsPageProps {
  setCurrentScript: (script: string) => void;
}
interface Script {
  id: number;
  title: string;
  content: string;
}
const ScriptsPage: React.FC<ScriptsPageProps> = ({
  setCurrentScript
}) => {
  const navigate = useNavigate();
  const [editingScript, setEditingScript] = useState<Script | null>(null);
  const [editedContent, setEditedContent] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showNewScriptMenu, setShowNewScriptMenu] = useState(false);
  const [swipedScriptId, setSwipedScriptId] = useState<number | null>(null);
  const touchStartXRef = useRef<number | null>(null);
  const sampleScripts = [{
    id: 1,
    title: 'Welcome Script',
    content: 'Welcome to the teleprompter app. This is a sample script that you can use to test the app.'
  }, {
    id: 2,
    title: 'Presentation Introduction',
    content: "Good morning everyone. Today I'll be presenting our quarterly results and discussing our plans for the next quarter."
  }, {
    id: 3,
    title: 'Video Script',
    content: "Hello viewers! In this video, we'll be exploring the new features of our teleprompter app and how you can use them to improve your presentations."
  }];
  const handleLoadScript = (script: Script) => {
    setCurrentScript(script.content);
    navigate('/');
  };
  const handleEditScript = (script: Script) => {
    setEditingScript(script);
    setEditedContent(script.content);
    setHasUnsavedChanges(false);
  };
  const handleSaveEdit = () => {
    // In a real app, this would save the changes to a database
    // For now, we'll just update the current script if it's the one being edited
    if (editingScript) {
      setCurrentScript(editedContent);
      setHasUnsavedChanges(false);
    }
  };
  const handleLoadEdit = () => {
    if (editingScript) {
      setCurrentScript(editedContent);
      setHasUnsavedChanges(false);
      navigate('/');
    }
  };
  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Do you want to save before exiting?')) {
        handleSaveEdit();
      }
    }
    setEditingScript(null);
    setEditedContent('');
  };
  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedContent(e.target.value);
    setHasUnsavedChanges(true);
  };
  const handleNewScriptClick = () => {
    setShowNewScriptMenu(!showNewScriptMenu);
  };
  const createNewScript = () => {
    const newScript = {
      id: Date.now(),
      title: 'New Script',
      content: ''
    };
    handleEditScript(newScript);
    setShowNewScriptMenu(false);
  };
  const importScript = () => {
    // In a real app, this would open a file picker
    alert('Import script functionality would be implemented here');
    setShowNewScriptMenu(false);
  };
  // Touch event handlers for swipe
  const handleTouchStart = (e: React.TouchEvent, scriptId: number) => {
    touchStartXRef.current = e.touches[0].clientX;
    setSwipedScriptId(null);
  };
  const handleTouchMove = (e: React.TouchEvent, scriptId: number) => {
    if (touchStartXRef.current === null) return;
    const touchEndX = e.touches[0].clientX;
    const diff = touchStartXRef.current - touchEndX;
    // If swiped left more than 50px, show the actions
    if (diff > 50) {
      setSwipedScriptId(scriptId);
    } else {
      setSwipedScriptId(null);
    }
  };
  const handleTouchEnd = () => {
    touchStartXRef.current = null;
  };
  const handleScriptClick = (script: Script) => {
    // Only navigate to edit if not in swipe mode
    if (swipedScriptId !== script.id) {
      handleEditScript(script);
    }
  };
  if (editingScript) {
    return <div className="flex-1 flex flex-col h-full" data-id="element-211">
        <PageHeader title="Edit Script" leftContent={<button onClick={handleCancelEdit} className="text-[#51a7fa] hover:text-white" data-id="element-213">
              <ChevronLeftIcon size={24} data-id="element-214" />
            </button>} rightContent={<div className="flex space-x-4" data-id="element-215">
              <button onClick={handleSaveEdit} className="text-[#51a7fa] hover:text-white" data-id="element-216">
                <SaveIcon size={24} data-id="element-217" />
              </button>
              <button onClick={handleLoadEdit} className="text-[#51a7fa] hover:text-white" data-id="element-218">
                <ImportIcon size={24} data-id="element-219" />
              </button>
            </div>} showBackButton={false} data-id="element-212" />
        <div className="px-6 py-2" data-id="element-220">
          <h2 className="text-lg font-semibold mb-3" data-id="element-221">{editingScript.title}</h2>
        </div>
        <div className="flex-1 px-6 pb-6" data-id="element-222">
          <textarea value={editedContent} onChange={handleContentChange} className="w-full h-full bg-gray-900 text-[#51a7fa] p-4 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter your script here..." data-id="element-223" />
        </div>
      </div>;
  }
  return <div className="flex-1 flex flex-col h-full" data-id="element-224">
      <PageHeader title="Scripts" rightContent={<div className="relative" data-id="element-226">
            <button onClick={handleNewScriptClick} className="text-[#51a7fa] hover:text-white" data-id="element-227">
              <PlusIcon size={24} data-id="element-228" />
            </button>
            {showNewScriptMenu && <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-md shadow-lg z-10" data-id="element-229">
                <div className="py-1" data-id="element-230">
                  <button onClick={createNewScript} className="block w-full text-left px-4 py-2 text-sm text-[#51a7fa] hover:bg-gray-700" data-id="element-231">
                    New Script
                  </button>
                  <button onClick={importScript} className="block w-full text-left px-4 py-2 text-sm text-[#51a7fa] hover:bg-gray-700" data-id="element-232">
                    Import Script
                  </button>
                </div>
              </div>}
          </div>} data-id="element-225" />
      <div className="flex-1 p-6 overflow-y-auto" data-id="element-233">
        <div className="space-y-4" data-id="element-234">
          {sampleScripts.map(script => <div key={script.id} className="relative overflow-hidden group" onTouchStart={e => handleTouchStart(e, script.id)} onTouchMove={e => handleTouchMove(e, script.id)} onTouchEnd={handleTouchEnd} onClick={() => handleScriptClick(script)} data-id="element-235">
              <div className={`bg-gray-900 p-4 rounded-lg cursor-pointer hover:bg-gray-800 transition-transform duration-300 ${swipedScriptId === script.id ? 'transform -translate-x-[150px]' : ''}`} data-id="element-236">
                <h2 className="text-lg font-semibold" data-id="element-237">{script.title}</h2>
                <p className="text-gray-400 truncate" data-id="element-238">{script.content}</p>
              </div>
              {/* Action buttons that appear on swipe or hover */}
              <div className={`absolute top-0 right-0 h-full flex items-center transform transition-transform duration-300 ${swipedScriptId === script.id ? 'translate-x-0' : 'translate-x-full'} group-hover:translate-x-0`} data-id="element-239">
                <button className="h-full bg-blue-900 w-[50px] text-white flex items-center justify-center text-xs px-1 py-6" onClick={e => {
              e.stopPropagation();
              handleLoadScript(script);
            }} data-id="element-240">
                  Load
                </button>
                <button className="h-full bg-gray-700 w-[50px] text-white flex items-center justify-center text-xs px-1 py-6" onClick={e => {
              e.stopPropagation();
              handleEditScript(script);
            }} data-id="element-241">
                  Edit
                </button>
                <button className="h-full bg-red-900 w-[50px] text-white flex items-center justify-center text-xs px-1 py-6" onClick={e => {
              e.stopPropagation();
              // Delete functionality would be implemented here
              alert(`Delete ${script.title}`);
            }} data-id="element-242">
                  Delete
                </button>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
};
export default ScriptsPage;